import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CarFront, ClipboardList, PlusCircle, ShieldCheck, UserRound } from 'lucide-react';
import PageHeader from '@/components/PageHeader.jsx';
import StickyActions from '@/components/StickyActions.jsx';
import { Button } from '@/components/ui/button.jsx';
import { confirmAction, showError, showSuccess } from '@/utils/alerts.js';
import QuickCreateChoferModal from '@/pages/Fojas/components/QuickCreateChoferModal.jsx';
import { useFlota } from './hooks/useFlota.js';
import {
  createAsignacion,
  fetchAsignaciones,
  fetchAsignacionesOpciones,
  finalizarAsignacion,
} from '../FlotaAsignaciones/services/asignaciones.service.js';
import { resolveEmpresaId, resolveSucursalActivaId } from '@/utils/requestContext.js';

const TIPOS = [
  { value: 'camion', label: 'Camión' },
  { value: 'utilitario', label: 'Utilitario' },
  { value: 'semi', label: 'Semi' },
  { value: 'acoplado', label: 'Acoplado' },
  { value: 'auto', label: 'Auto' },
  { value: 'moto', label: 'Moto' },
  { value: 'otro', label: 'Otro' },
];

const ESTADOS = [
  { value: 'activo', label: 'Activo' },
  { value: 'inactivo', label: 'Inactivo' },
  { value: 'mantenimiento', label: 'Mantenimiento' },
];

const PATENTE_REGEX_MODERNA = /^[A-Z]{2}[0-9]{3}[A-Z]{2}$/;
const PATENTE_REGEX_LEGACY = /^[A-Z]{3}[0-9]{3}$/;

const initialForm = {
  patente: '',
  tipo: 'camion',
  marca: '',
  modelo: '',
  anio: '',
  capacidad_kg: '',
  capacidad_m3: '',
  estado: 'activo',
  sucursal_id: '',
  kilometraje: '',
  fecha_vtv: '',
  fecha_seguro: '',
  fecha_service: '',
  observaciones: '',
  chofer_id: '',
};

const inputClassName =
  'w-full rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.92)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]';

function Field({ label, hint, error, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm font-semibold text-[rgb(var(--fg))]">{label}</label>
        {hint ? <span className="text-xs text-[rgb(var(--muted))]">{hint}</span> : null}
      </div>
      {children}
      {error ? <p className="text-xs text-red-500">{error}</p> : null}
    </div>
  );
}

function buildChoferLabel(chofer) {
  if (!chofer) return '';
  const fullName = [chofer.apellido, chofer.nombre].filter(Boolean).join(', ');
  if (!fullName) return `Chofer #${chofer.id}`;
  return chofer.nroDocumento ? `${fullName} · DNI ${chofer.nroDocumento}` : fullName;
}

function normalizePatenteInput(value) {
  return String(value || '')
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 7);
}

function isValidPatente(value) {
  return PATENTE_REGEX_MODERNA.test(value) || PATENTE_REGEX_LEGACY.test(value);
}

function getInlineErrorMessage(error, fallbackMessage) {
  return error?.message || error?.payload?.message || error?.payload?.error || fallbackMessage;
}

function getGeneralErrorMessage(error, fallbackMessage) {
  if (!error) return fallbackMessage;
  if (Number(error?.status) >= 500) return fallbackMessage;
  return getInlineErrorMessage(error, fallbackMessage);
}

export default function FormularioFlota() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const { loading, error, getById, create, update, deactivate } = useFlota();
  const [form, setForm] = useState(() => ({
    ...initialForm,
    sucursal_id: String(resolveSucursalActivaId() || ''),
  }));
  const [errors, setErrors] = useState({});
  const [assignmentSuccessMessage, setAssignmentSuccessMessage] = useState('');
  const [generalMessage, setGeneralMessage] = useState('');
  const [choferes, setChoferes] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [showQuickChofer, setShowQuickChofer] = useState(false);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const patenteInputRef = useRef(null);
  const choferSelectRef = useRef(null);

  const empresaId = resolveEmpresaId();
  const sucursalActivaId = resolveSucursalActivaId();

  useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const data = await fetchAsignacionesOpciones();
        if (cancelled) return;
        setChoferes(Array.isArray(data?.choferes) ? data.choferes : []);
      } catch (optionsError) {
        console.error('No se pudieron cargar choferes para flota', optionsError);
        if (!cancelled) setChoferes([]);
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    };
    loadOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEditing) return undefined;
    let cancelled = false;
    const loadVehiculo = async () => {
      const [vehiculo, asignaciones] = await Promise.all([
        getById(id),
        fetchAsignaciones({ vehiculo_id: id, estado: 'ACTIVA', limit: 1 }).catch(() => []),
      ]);
      if (!vehiculo || cancelled) return;

      const activeAssignment = Array.isArray(asignaciones) ? asignaciones[0] || null : null;
      setCurrentAssignment(activeAssignment);
      setForm({
        patente: vehiculo.patente || '',
        tipo: vehiculo.tipo || 'camion',
        marca: vehiculo.marca || '',
        modelo: vehiculo.modelo || '',
        anio: vehiculo.anio ?? '',
        capacidad_kg: vehiculo.capacidad_kg ?? '',
        capacidad_m3: vehiculo.capacidad_m3 ?? '',
        estado: vehiculo.estado || 'activo',
        sucursal_id: String(vehiculo.sucursal_id ?? sucursalActivaId ?? ''),
        kilometraje: vehiculo.kilometraje ?? '',
        fecha_vtv: vehiculo.fecha_vtv || '',
        fecha_seguro: vehiculo.fecha_seguro || '',
        fecha_service: vehiculo.fecha_service || '',
        observaciones: vehiculo.observaciones || '',
        chofer_id: String(activeAssignment?.chofer_id || vehiculo.chofer_id || ''),
      });
    };
    loadVehiculo();
    return () => {
      cancelled = true;
    };
  }, [getById, id, isEditing, sucursalActivaId]);

  const selectedChofer = useMemo(
    () => choferes.find((item) => String(item.id) === String(form.chofer_id)) || null,
    [choferes, form.chofer_id],
  );

  const handleChange = (field) => (event) => {
    const nextValue = field === 'patente' ? normalizePatenteInput(event.target.value) : event.target.value;
    setForm((prev) => ({ ...prev, [field]: nextValue }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
    if (field === 'chofer_id') {
      setAssignmentSuccessMessage('');
    }
    setGeneralMessage('');
  };

  const validate = () => {
    const nextErrors = {};
    const patenteNormalizada = normalizePatenteInput(form.patente);

    if (!patenteNormalizada) nextErrors.patente = 'La patente es obligatoria.';
    else if (!isValidPatente(patenteNormalizada)) nextErrors.patente = 'Formato de patente inválido. Usá el formato AB987HL.';
    if (!form.tipo) nextErrors.tipo = 'El tipo de vehículo es obligatorio.';
    if (!form.sucursal_id) nextErrors.sucursal_id = 'No pudimos resolver la sucursal activa.';
    if (form.anio && (Number(form.anio) < 1900 || Number(form.anio) > 2100)) nextErrors.anio = 'Ingresá un año válido.';
    if (form.kilometraje && Number(form.kilometraje) < 0) nextErrors.kilometraje = 'El kilometraje no puede ser negativo.';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const payload = useMemo(
    () => ({
      patente: normalizePatenteInput(form.patente),
      tipo: form.tipo,
      marca: form.marca.trim() || null,
      modelo: form.modelo.trim() || null,
      anio: form.anio ? Number(form.anio) : null,
      capacidad_kg: form.capacidad_kg ? Number(form.capacidad_kg) : null,
      capacidad_m3: form.capacidad_m3 ? Number(form.capacidad_m3) : null,
      estado: form.estado,
      sucursal_id: form.sucursal_id ? Number(form.sucursal_id) : null,
      kilometraje: form.kilometraje ? Number(form.kilometraje) : null,
      fecha_vtv: form.fecha_vtv || null,
      fecha_seguro: form.fecha_seguro || null,
      fecha_service: form.fecha_service || null,
      observaciones: form.observaciones.trim() || null,
    }),
    [form],
  );

  const isSubmitDisabled = loading || isSubmitting || !normalizePatenteInput(form.patente) || !form.tipo;

  const syncAssignment = async (vehiculoId) => {
    const selectedChoferId = form.chofer_id ? Number(form.chofer_id) : null;
    const currentChoferId = currentAssignment?.chofer_id ? Number(currentAssignment.chofer_id) : null;

    if (currentAssignment?.id && currentChoferId !== selectedChoferId) {
      await finalizarAsignacion(currentAssignment.id, {
        fecha_hasta: new Date().toISOString().slice(0, 10),
        estado: 'CERRADA',
      });
    }

    if (selectedChoferId && selectedChoferId !== currentChoferId) {
      const assignmentPayload = {
        chofer_id: selectedChoferId,
        vehiculo_id: Number(vehiculoId),
        sucursal_id: Number(form.sucursal_id || sucursalActivaId || 0) || null,
        fecha_desde: new Date().toISOString().slice(0, 10),
        observaciones: 'Asignación generada desde administración de flota.',
      };

      if (import.meta.env.DEV) {
        console.log('POST /api/flota/asignaciones payload', assignmentPayload);
      }

      const createdAssignment = await createAsignacion(assignmentPayload);
      setCurrentAssignment(createdAssignment || null);
      setAssignmentSuccessMessage('Chofer asignado correctamente.');
    }

    if (!selectedChoferId) {
      setCurrentAssignment(null);
      setAssignmentSuccessMessage('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    if (!validate()) return;

    setAssignmentSuccessMessage('');
    setGeneralMessage('');
    setErrors((prev) => ({ ...prev, patente: '', chofer_id: '' }));
    setIsSubmitting(true);

    try {
      if (import.meta.env.DEV) {
        console.log(`${isEditing ? 'PUT' : 'POST'} /api/flota payload`, payload);
      }

      const result = isEditing
        ? await update(id, payload, { throwOnError: true })
        : await create(payload, { throwOnError: true });
      const vehiculoId = result?.id || id;

      try {
        await syncAssignment(vehiculoId);
      } catch (assignmentError) {
        if (import.meta.env.DEV) {
          console.error('Error real de asignación en flota', assignmentError?.payload || assignmentError);
        }
        setErrors((prev) => ({
          ...prev,
          chofer_id: getInlineErrorMessage(
            assignmentError,
            'No pudimos asignar el chofer. Revisá el bloque y volvé a intentar.',
          ),
        }));
        setGeneralMessage('El vehículo se guardó. Falta resolver la asignación del chofer.');
        choferSelectRef.current?.focus();
        return;
      }

      setGeneralMessage(isEditing ? 'Vehículo actualizado correctamente.' : 'Vehículo creado correctamente.');
      setTimeout(() => navigate('/administracion/flota'), 600);
    } catch (submitError) {
      if (import.meta.env.DEV) {
        console.error('Error real de alta de vehículo', submitError?.payload || submitError);
      }

      if (submitError?.field === 'patente') {
        setErrors((prev) => ({
          ...prev,
          patente: getInlineErrorMessage(submitError, 'La patente ya existe en la flota.'),
        }));
        patenteInputRef.current?.focus();
        return;
      }

      setGeneralMessage(getGeneralErrorMessage(submitError, 'No se pudo guardar el vehículo.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    const decision = await confirmAction({
      title: '¿Desactivar vehículo?',
      text: `Se marcará como inactivo el vehículo ${form.patente}.`,
      confirmText: 'Desactivar',
    });
    if (!decision.isConfirmed) return;

    try {
      const result = await deactivate(id, { throwOnError: true });
      if (!result) return;
      await showSuccess('Listo', `El vehículo ${form.patente} fue desactivado.`);
      navigate('/administracion/flota');
    } catch (deactivateError) {
      await showError('Error', deactivateError?.message || 'No se pudo desactivar el vehículo.');
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,rgba(var(--bg),0.98),rgba(var(--panel),0.92))] pb-16">
      <div className="mx-auto max-w-[1240px] px-4 pb-10 pt-8">
        <PageHeader
          title={isEditing ? 'Editar vehículo' : 'Nuevo vehículo'}
          subtitle="Configurá la unidad operativa y, si corresponde, dejala vinculada al chofer activo de la sucursal."
        />

        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="grid gap-4 lg:grid-cols-[1.3fr_0.7fr]">
            <article className="rounded-[28px] border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.94)] p-6 shadow-[0_24px_60px_-30px_rgba(var(--shadow),0.45)]">
              <div className="mb-6 flex items-start gap-3">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-[rgba(var(--brand),0.12)] p-3 text-[rgb(var(--brand))]">
                    <CarFront className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Ficha de unidad</h2>
                    <p className="text-sm text-[rgb(var(--muted))]">Datos operativos reales de la flota por sucursal activa.</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-4">
                  <Field label="Patente" hint="Formato esperado: AB987HL" error={errors.patente}>
                    <input
                      ref={patenteInputRef}
                      value={form.patente}
                      onChange={handleChange('patente')}
                      className={inputClassName}
                      placeholder="AB987HL"
                      maxLength={7}
                      autoComplete="off"
                    />
                  </Field>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <Field label="Tipo de vehículo">
                    <select value={form.tipo} onChange={handleChange('tipo')} className={inputClassName}>
                      {TIPOS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <Field label="Estado">
                    <select value={form.estado} onChange={handleChange('estado')} className={inputClassName}>
                      {ESTADOS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <Field label="Marca">
                    <input value={form.marca} onChange={handleChange('marca')} className={inputClassName} />
                  </Field>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <Field label="Modelo">
                    <input value={form.modelo} onChange={handleChange('modelo')} className={inputClassName} />
                  </Field>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <Field label="Año" error={errors.anio}>
                    <input type="number" min="1900" max="2100" value={form.anio} onChange={handleChange('anio')} className={inputClassName} />
                  </Field>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <Field label="Capacidad (kg)">
                    <input type="number" min="0" value={form.capacidad_kg} onChange={handleChange('capacidad_kg')} className={inputClassName} />
                  </Field>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <Field label="Capacidad (m3)">
                    <input type="number" min="0" step="0.01" value={form.capacidad_m3} onChange={handleChange('capacidad_m3')} className={inputClassName} />
                  </Field>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <Field label="Kilometraje" error={errors.kilometraje}>
                    <input type="number" min="0" value={form.kilometraje} onChange={handleChange('kilometraje')} className={inputClassName} />
                  </Field>
                </div>
              </div>
            </article>

            <article className="rounded-[28px] border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.94)] p-6 shadow-[0_24px_60px_-30px_rgba(var(--shadow),0.45)]">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-2xl bg-[rgba(var(--brand),0.12)] p-3 text-[rgb(var(--brand))]">
                  <UserRound className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Chofer asignado</h2>
                  <p className="text-sm text-[rgb(var(--muted))]">Vinculación operativa con alta rápida.</p>
                </div>
              </div>

              <Field
                label="Chofer"
                hint={loadingOptions ? 'Cargando padrón...' : `${choferes.length} disponibles`}
                error={errors.chofer_id}
              >
                <select ref={choferSelectRef} value={form.chofer_id} onChange={handleChange('chofer_id')} className={inputClassName}>
                  <option value="">{loadingOptions ? 'Cargando choferes...' : 'Sin asignar'}</option>
                  {choferes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {buildChoferLabel(item)}
                    </option>
                  ))}
                </select>
              </Field>
              {selectedChofer ? <p className="mt-2 text-xs text-[rgb(var(--muted))]">{buildChoferLabel(selectedChofer)}</p> : null}
              {assignmentSuccessMessage ? <p className="mt-2 text-xs text-[rgb(var(--brand-700))]">{assignmentSuccessMessage}</p> : null}

              <div className="mt-4 flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => setShowQuickChofer(true)} disabled={isSubmitting}>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Alta rápida de chofer
                </Button>
              </div>
            </article>
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <article className="rounded-[28px] border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.94)] p-6 shadow-[0_24px_60px_-30px_rgba(var(--shadow),0.45)]">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-2xl bg-[rgba(var(--brand),0.12)] p-3 text-[rgb(var(--brand))]">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Vencimientos</h2>
                  <p className="text-sm text-[rgb(var(--muted))]">Control documental básico de la unidad.</p>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-4">
                  <Field label="VTV">
                    <input type="date" value={form.fecha_vtv} onChange={handleChange('fecha_vtv')} className={inputClassName} />
                  </Field>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <Field label="Seguro">
                    <input type="date" value={form.fecha_seguro} onChange={handleChange('fecha_seguro')} className={inputClassName} />
                  </Field>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <Field label="Service">
                    <input type="date" value={form.fecha_service} onChange={handleChange('fecha_service')} className={inputClassName} />
                  </Field>
                </div>
              </div>
            </article>

            <article className="rounded-[28px] border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.94)] p-6 shadow-[0_24px_60px_-30px_rgba(var(--shadow),0.45)]">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-2xl bg-[rgba(var(--brand),0.12)] p-3 text-[rgb(var(--brand))]">
                  <ClipboardList className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Observaciones</h2>
                  <p className="text-sm text-[rgb(var(--muted))]">Notas internas, uso o restricciones.</p>
                </div>
              </div>
              <Field label="Detalle">
                <textarea rows={5} value={form.observaciones} onChange={handleChange('observaciones')} className={inputClassName} />
              </Field>
            </article>
          </section>

          <StickyActions
            left={
              <div className="flex flex-wrap items-center gap-3">
                {isEditing ? (
                  <button
                    type="button"
                    onClick={handleDeactivate}
                    className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  >
                    Desactivar
                  </button>
                ) : null}
                {(generalMessage || error) ? (
                  <p className={`text-sm ${error ? 'text-red-500' : 'text-[rgb(var(--brand-700))]'}`}>
                    {error || generalMessage}
                  </p>
                ) : null}
              </div>
            }
            right={[
              <button
                key="cancel"
                type="button"
                onClick={() => navigate('/administracion/flota')}
                className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                Cancelar
              </button>,
              <button
                key="save"
                type="submit"
                disabled={isSubmitDisabled}
                className="rounded-xl bg-[rgb(var(--brand))] px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))] disabled:opacity-60"
              >
                {isSubmitting ? 'Guardando...' : 'Guardar'}
              </button>,
            ]}
          />
        </form>
      </div>

      {showQuickChofer ? (
        <QuickCreateChoferModal
          isOpen={showQuickChofer}
          onClose={() => setShowQuickChofer(false)}
          empresaId={empresaId}
          sucursalId={Number(form.sucursal_id || sucursalActivaId || 0) || null}
          onCreated={(chofer) => {
            setChoferes((prev) => [chofer, ...prev.filter((item) => String(item.id) !== String(chofer.id))]);
            setForm((prev) => ({ ...prev, chofer_id: String(chofer.id) }));
            setShowQuickChofer(false);
          }}
        />
      ) : null}
    </div>
  );
}
